/* image-loading.js ─ orchestrates brand-tuned image loaders ────────────────
   Reads .img-load wrappers, watches their child <img class="img-load__real">,
   adds .is-loading immediately and .is-loaded once the image has decoded.

   Special rules:
   - Slow connections (downlink ≤ 1 Mbps OR saveData) → swap any non-shimmer
     loader to the dial variant before mounting.
   - PDP main image with data-loader-once-per-product:
       First sight per (sessionStorage flag) → keep configured loader (knit-grid)
       After that                             → swap to blur variant
   - data-loader="tiles" placeholder requires JS-injected child tiles.
   - data-loader="knit-grid" placeholder requires JS-injected SVG path grid.
   - data-loader="wipe" placeholder requires JS-injected column children.

   Public API (window.BoniImageLoader):
     mount(node)             - mount a single .img-load (idempotent)
     mountAll(root = doc)    - mount all unmounted .img-load under root
     transitionTo(node, src) - swap the <img> in node to a new URL with the
                               currently configured loader replaying.
*/
(function () {
  'use strict';

  const SLOW = (() => {
    try {
      const c = navigator.connection;
      if (!c) return false;
      if (c.saveData) return true;
      if (typeof c.downlink === 'number' && c.downlink <= 1) return true;
      if (c.effectiveType && /^(slow-2g|2g)$/.test(c.effectiveType)) return true;
    } catch (_) {}
    return false;
  })();

  const KNIT_COLS = 8;
  const KNIT_ROWS = 12;
  const TILE_DIM  = 3; // 3×3 = 9
  const WIPE_COLS = 14;

  function buildKnitGrid(svg) {
    const W = 320, H = 400;
    const cellW = W / KNIT_COLS, cellH = H / KNIT_ROWS;
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    for (let r = 0; r < KNIT_ROWS; r++) {
      for (let c = 0; c < KNIT_COLS; c++) {
        const cx = c * cellW + cellW / 2;
        const top = r * cellH + 4;
        const bot = (r + 1) * cellH - 4;
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', `M ${c*cellW + 4} ${bot} L ${cx} ${top} L ${(c+1)*cellW - 4} ${bot}`);
        path.style.animationDelay = `${((r + c) / (KNIT_ROWS + KNIT_COLS)) * 1.0}s`;
        svg.appendChild(path);
      }
    }
  }

  function buildTiles(node, src) {
    // remove any old tiles
    node.querySelectorAll('.img-load__tile').forEach(t => t.remove());
    const real = node.querySelector('.img-load__real');
    for (let r = 0; r < TILE_DIM; r++) {
      for (let c = 0; c < TILE_DIM; c++) {
        const div = document.createElement('div');
        div.className = 'img-load__tile';
        div.style.left   = `${(c * 100) / TILE_DIM}%`;
        div.style.top    = `${(r * 100) / TILE_DIM}%`;
        div.style.width  = `${100 / TILE_DIM}%`;
        div.style.height = `${100 / TILE_DIM}%`;
        div.style.backgroundImage = `url(${src})`;
        div.style.backgroundPosition = `${(c * 100) / (TILE_DIM - 1)}% ${(r * 100) / (TILE_DIM - 1)}%`;
        div.style.transitionDelay = `${(r * TILE_DIM + c) * 80}ms`;
        node.insertBefore(div, real);
      }
    }
  }

  function buildWipe(placeholder) {
    placeholder.innerHTML = '';
    for (let i = 0; i < WIPE_COLS; i++) {
      const col = document.createElement('span');
      col.className = 'img-load__col';
      col.style.transitionDelay = `${i * 40}ms`;
      placeholder.appendChild(col);
    }
  }

  function buildDial(placeholder) {
    placeholder.innerHTML = `
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle class="track" cx="50" cy="50" r="45"></circle>
        <circle class="fill"  cx="50" cy="50" r="45"></circle>
      </svg>`;
  }

  function buildPaperReveal(placeholder, node) {
    placeholder.innerHTML = '';
    const top  = document.createElement('div'); top.className = 'img-load__sheet img-load__sheet--top';
    const bot  = document.createElement('div'); bot.className = 'img-load__sheet img-load__sheet--bot';
    const seam = document.createElement('div'); seam.className = 'img-load__seam';
    node.insertBefore(top, placeholder);
    node.insertBefore(bot, placeholder);
    node.insertBefore(seam, placeholder);
  }

  function applyVariant(node, variant) {
    // wipe any previous variant class
    [...node.classList].forEach(c => { if (c.startsWith('img-load--')) node.classList.remove(c); });
    node.classList.add('img-load', `img-load--${variant}`);
    node.dataset.loader = variant;
  }

  function setupPlaceholderFor(node, variant) {
    let ph = node.querySelector('.img-load__placeholder');
    if (!ph) {
      ph = document.createElement('div');
      ph.className = 'img-load__placeholder';
      const real = node.querySelector('.img-load__real');
      node.insertBefore(ph, real || node.firstChild);
    } else {
      ph.innerHTML = '';
    }
    // remove sheets/seams from prior paper-reveal mount
    node.querySelectorAll('.img-load__sheet, .img-load__seam').forEach(el => el.remove());

    const real = node.querySelector('.img-load__real');
    const src  = real ? (real.currentSrc || real.src) : node.dataset.imgSrc || '';

    if (variant === 'knit-grid') {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      buildKnitGrid(svg);
      ph.appendChild(svg);
    } else if (variant === 'tiles') {
      buildTiles(node, src);
    } else if (variant === 'wipe') {
      buildWipe(ph);
    } else if (variant === 'dial') {
      buildDial(ph);
    } else if (variant === 'paper-reveal') {
      buildPaperReveal(ph, node);
    } else if (variant === 'blur') {
      const lqip = node.dataset.lqip;
      if (lqip) ph.style.background = lqip;
    }
    // shimmer needs nothing extra
  }

  // Minimum on-screen time per variant so the animation actually plays
  // even when the image is cached. Knit-grid needs ~1.5s to draw all
  // 96 stitches; tiles needs ~1.3s for the staggered mosaic.
  const MIN_HOLD = {
    'knit-grid': 1500,
    'tiles': 1300,
    'paper-reveal': 1100,
    'wipe': 800,
    'blur': 600,
    'shimmer': 700,
    'dial': 1200,
  };

  function watchImage(node) {
    const real = node.querySelector('.img-load__real');
    if (!real) return;
    node.classList.add('is-loading');

    const variant  = node.dataset.loader || 'blur';
    const minHold  = MIN_HOLD[variant] || 220;
    const start    = performance.now();

    const finish = () => {
      const elapsed = performance.now() - start;
      const wait    = Math.max(0, minHold - elapsed);
      setTimeout(() => {
        node.classList.remove('is-loading');
        node.classList.add('is-loaded');
      }, wait);
    };

    if (real.complete && real.naturalWidth > 0) {
      finish();
    } else {
      real.addEventListener('load', finish, { once: true });
      real.addEventListener('error', finish, { once: true });
    }
  }

  function effectiveVariant(node) {
    let variant = node.dataset.loader || 'blur';

    // Slow-connection override: dial supersedes everything except shimmer
    // (shimmer is JS-free and harmless on slow connections).
    if (SLOW && variant !== 'shimmer' && variant !== 'dial') {
      variant = 'dial';
    }

    // PDP first-time-per-product: knit-grid first, blur after.
    if (node.dataset.loaderOncePerProduct) {
      const key = `boni:loader:${node.dataset.loaderOncePerProduct}`;
      let alreadySeen = false;
      try { alreadySeen = sessionStorage.getItem(key) === '1'; } catch (_) {}
      if (!alreadySeen) {
        try { sessionStorage.setItem(key, '1'); } catch (_) {}
        // keep configured variant (knit-grid)
      } else {
        variant = SLOW ? 'dial' : 'blur';
      }
    }

    return variant;
  }

  function mount(node) {
    if (!node || node.dataset.loaderMounted === '1') return;
    node.dataset.loaderMounted = '1';

    const variant = effectiveVariant(node);
    applyVariant(node, variant);
    setupPlaceholderFor(node, variant);
    watchImage(node);
  }

  function mountAll(root) {
    (root || document).querySelectorAll('.img-load').forEach(mount);
  }

  /* Replace the <img src> in a mounted loader and replay the animation.
     Used by the gallery thumbnail switcher. */
  function transitionTo(node, src, srcset, sizes) {
    if (!node) return;
    const real = node.querySelector('.img-load__real');
    if (!real) return;

    const variant = node.dataset.loader || 'tiles';
    node.classList.remove('is-loaded');
    // Reflow before re-adding loading class to restart animations
    void node.offsetWidth;

    // Refresh placeholder for tile variant (needs new bg image)
    setupPlaceholderFor(node, variant);

    if (srcset) real.srcset = srcset;
    if (sizes)  real.sizes  = sizes;
    real.src = src;

    watchImage(node);
  }

  window.BoniImageLoader = { mount, mountAll, transitionTo };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll());
  } else {
    mountAll();
  }
})();
